import {
    isValidCheckLatitude,
    isValidCheckLongitude,
    toCheckLocationNumber,
} from "../helpers/event-check-location-helper";
import {
    DEFAULT_PHOTON_LANGUAGE,
    EUROPEAN_COUNTRY_CODES,
    PHOTON_SUPPORTED_LANGUAGES,
} from "./exhibition-location-map-const";


export const normalizeSearchQuery = (value) => (
    String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase()
);

export const getPhotonLanguage = (languageSlug) => {
    const language = String(languageSlug || "")
        .trim()
        .toLowerCase()
        .split(/[-_]/)[ 0 ];

    return PHOTON_SUPPORTED_LANGUAGES.includes(language) ? language : DEFAULT_PHOTON_LANGUAGE;
};

export const getCountryOptions = (language) => {
    let regionNames;

    if (typeof Intl.DisplayNames === "function")
    {
        regionNames = new Intl.DisplayNames([ language ], { type: "region" });
    }

    return EUROPEAN_COUNTRY_CODES
        .map((countryCode) => ({
            value: countryCode,
            label: regionNames?.of(countryCode) || countryCode,
        }))
        .sort((firstCountry, secondCountry) => (
            firstCountry.label.localeCompare(secondCountry.label, language)
        ));
};

export const getPhotonCoordinates = (feature) => {
    const coordinates = feature?.geometry?.coordinates || [];
    const longitude = toCheckLocationNumber(coordinates[ 0 ]);
    const latitude = toCheckLocationNumber(coordinates[ 1 ]);

    return isValidCheckLatitude(latitude) && isValidCheckLongitude(longitude) ? [ latitude, longitude ] : null;
};

export const getPhotonExtentBounds = (feature) => {
    const extent = feature?.properties?.extent || [];

    if (!Array.isArray(extent) || extent.length < 4)
    {
        return null;
    }

    const west = toCheckLocationNumber(extent[ 0 ]);
    const firstLatitude = toCheckLocationNumber(extent[ 1 ]);
    const east = toCheckLocationNumber(extent[ 2 ]);
    const secondLatitude = toCheckLocationNumber(extent[ 3 ]);

    if (
        !isValidCheckLongitude(west) ||
        !isValidCheckLatitude(firstLatitude) ||
        !isValidCheckLongitude(east) ||
        !isValidCheckLatitude(secondLatitude)
    )
    {
        return null;
    }

    return [
        [ firstLatitude, west ],
        [ secondLatitude, east ],
    ];
};

export const getPhotonFeatureLabel = (feature) => {
    const properties = feature?.properties || {};
    const cityLine = [
        properties.postcode,
        properties.city || properties.locality || properties.county,
    ].filter(Boolean).join(" ");
    const addressParts = [
        properties.street && properties.housenumber ?
            `${ properties.street } ${ properties.housenumber }` :
            properties.street,
        cityLine,
        properties.state,
        properties.country,
    ].filter(Boolean);
    const address = addressParts.join(", ");
    const name = properties.name || address;

    return name && address && name !== address ? `${ name } - ${ address }` : (name || address);
};

export const mapPhotonFeatureToOption = (feature, index) => {
    const center = getPhotonCoordinates(feature);
    const label = getPhotonFeatureLabel(feature);

    if (!center || !label)
    {
        return null;
    }

    return {
        value: label,
        label,
        feature,
        key: `${ feature?.properties?.osm_type || "feature" }-${ feature?.properties?.osm_id || index }-${ center.join(",") }`,
    };
};

const getPhotonOptionScore = (option, normalizedQuery) => {
    const properties = option?.feature?.properties || {};
    const normalizedName = normalizeSearchQuery(properties.name);
    const normalizedLabel = normalizeSearchQuery(option?.label);
    const queryWords = normalizedQuery.split(" ").filter(Boolean);

    let score = 0;

    if (normalizedName === normalizedQuery) score += 100;
    if (normalizedName.includes(normalizedQuery)) score += 80;

    queryWords.forEach((word) => {
        if (normalizedName.includes(word)) score += 15;
        if (normalizedLabel.includes(word)) score += 5;
    });

    if (properties.extent?.length) score += 3;

    return score;
};

export const sortPhotonOptions = (options, normalizedQuery) => (
    [ ...options ].sort((firstOption, secondOption) => (
        getPhotonOptionScore(secondOption, normalizedQuery) - getPhotonOptionScore(firstOption, normalizedQuery)
    ))
);
